import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getPolicy, getPolicyRevisions, getLinkedItems, getPolicyAcknowledgmentStats, acknowledgePolicy } from '../../lib/policies';
import { useAuth } from '../../hooks/useAuth';

const STATUS_META = {
  current:      { label: 'Current',      bg: 'var(--success-bg)', color: 'var(--success)' },
  needs_review: { label: 'Needs Review', bg: 'var(--warn-bg)',    color: 'var(--warn)' },
  draft:        { label: 'Draft',        bg: 'var(--bg2)',        color: 'var(--text3)' },
};

export default function PolicyDetail() {
  const { sectionNumber } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [policy, setPolicy] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [linked, setLinked] = useState({ sops: [], forms: [], referenceMaterials: [] });
  const [ackStats, setAckStats] = useState({ total: 0, acknowledged: 0, pct: 0, acks: [] });
  const [loading, setLoading] = useState(true);
  const [acking, setAcking] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { load(); }, [sectionNumber]);

  const load = async () => {
    setLoading(true);
    setError('');
    const { data: p, error: err } = await getPolicy(sectionNumber);
    if (err || !p) { setError('Policy section not found.'); setLoading(false); return; }
    setPolicy(p);
    const [{ data: revs }, li, stats] = await Promise.all([
      getPolicyRevisions(p.id),
      getLinkedItems(p.id),
      getPolicyAcknowledgmentStats(p.section_number, p.version),
    ]);
    setRevisions(revs || []);
    setLinked(li);
    setAckStats(stats);
    setLoading(false);
  };

  const handleAcknowledge = async () => {
    if (!policy || !profile?.id) return;
    setAcking(true);
    // profile.id is the logged-in user's auth id; staff.profile_id is the reverse FK,
    // so resolve the staff row for this profile before recording the acknowledgment.
    const { supabase } = await import('../../lib/supabase');
    const { data: staffRow } = await supabase.from('staff').select('id').eq('profile_id', profile.id).maybeSingle();
    if (!staffRow) { setAcking(false); return; }
    await acknowledgePolicy(policy.id, policy.version, staffRow.id, profile.full_name);
    const stats = await getPolicyAcknowledgmentStats(policy.section_number, policy.version);
    setAckStats(stats);
    setAcking(false);
  };

  if (loading) return <div className="empty-state"><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>;
  if (error || !policy) return <div className="empty-state">{error || 'Not found.'}</div>;

  const meta = STATUS_META[policy.status] || STATUS_META.draft;
  const iAcknowledged = ackStats.acks.some(a => a.staff?.full_name === profile?.full_name);

  return (
    <div>
      <Link to="/policies" style={{ fontSize: 12, color: 'var(--text3)', textDecoration: 'none', display: 'inline-block', marginBottom: 10 }}>← Back to Policies & Procedures</Link>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 16, alignItems: 'start' }}>
        <div className="card">
          <div style={{ padding: '16px 18px', borderBottom: '0.5px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text3)', fontFamily: 'monospace' }}>{policy.section_number}</span>
              <span style={{ fontSize: 11, fontWeight: 500, padding: '2px 9px', borderRadius: 10, background: meta.bg, color: meta.color }}>{meta.label}</span>
              <span style={{ fontSize: 11, color: 'var(--text3)' }}>v{policy.version}</span>
            </div>
            <div style={{ fontSize: 18, fontWeight: 500, color: 'var(--text)', marginBottom: 8 }}>{policy.title}</div>
            <div style={{ display: 'flex', gap: 16, fontSize: 11, color: 'var(--text3)', flexWrap: 'wrap' }}>
              {policy.effective_date && <span>Effective {policy.effective_date}</span>}
              {policy.review_due_date && <span>Review due {policy.review_due_date}</span>}
            </div>
            {policy.regulatory_tags?.length > 0 && (
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 8 }}>
                {policy.regulatory_tags.map(t => (
                  <span key={t} style={{ fontSize: 10, padding: '1px 7px', borderRadius: 4, background: 'var(--accent-bg)', color: 'var(--accent2)', border: '0.5px solid var(--accent-border)' }}>{t}</span>
                ))}
              </div>
            )}
          </div>

          {(linked.sops.length > 0 || linked.forms.length > 0 || linked.referenceMaterials.length > 0) && (
            <div style={{ padding: '12px 18px', borderBottom: '0.5px solid var(--border)', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {linked.sops.map(s => (
                <span key={s.id} style={{ fontSize: 11, padding: '3px 9px', borderRadius: 12, background: 'var(--bg2)', color: 'var(--text2)', border: '0.5px solid var(--border)' }}>SOP · {s.code}</span>
              ))}
              {linked.forms.map(f => (
                <span key={f.id} style={{ fontSize: 11, padding: '3px 9px', borderRadius: 12, background: 'var(--bg2)', color: 'var(--text2)', border: '0.5px solid var(--border)' }}>Form · {f.title}</span>
              ))}
              {linked.referenceMaterials.map(r => (
                <span key={r.id} style={{ fontSize: 11, padding: '3px 9px', borderRadius: 12, background: 'var(--bg2)', color: 'var(--text2)', border: '0.5px solid var(--border)' }}>Reference · {r.title}</span>
              ))}
            </div>
          )}

          <div style={{ padding: '16px 18px', fontSize: 13.5, lineHeight: 1.7, color: 'var(--text2)' }} className="policy-markdown">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{policy.body_content}</ReactMarkdown>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="card" style={{ padding: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Staff Acknowledgment</div>
            <div style={{ fontSize: 22, fontWeight: 500, color: 'var(--text)' }}>{ackStats.pct}%</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10 }}>{ackStats.acknowledged} of {ackStats.total} active staff</div>
            {!iAcknowledged ? (
              <button className="btn btn-primary btn-sm" style={{ width: '100%' }} onClick={handleAcknowledge} disabled={acking}>
                {acking ? 'Saving…' : 'Acknowledge this policy'}
              </button>
            ) : (
              <div style={{ fontSize: 12, color: 'var(--success)' }}>✓ You've acknowledged this version</div>
            )}
          </div>

          <div className="card" style={{ padding: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Revision History</div>
            {revisions.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--text3)' }}>No revisions on file yet.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {revisions.map(r => (
                  <div key={r.id} style={{ fontSize: 12, borderLeft: '2px solid var(--border)', paddingLeft: 8 }}>
                    <div style={{ fontWeight: 500, color: 'var(--text)' }}>v{r.version}</div>
                    <div style={{ color: 'var(--text3)' }}>{new Date(r.changed_at).toLocaleDateString()} · {r.changed_by?.full_name || 'Unknown'}</div>
                    {r.summary && <div style={{ color: 'var(--text2)', marginTop: 2 }}>{r.summary}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
